import { useState, useRef, useCallback } from "react";

interface ImageMagnifierProps {
  src: string;
  alt: string;
  magnifierSize?: number;
  zoomLevel?: number;
  className?: string;
}

export const ImageMagnifier = ({
  src,
  alt,
  magnifierSize = 250,
  zoomLevel = 3,
  className = "",
}: ImageMagnifierProps) => {
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0 });
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = useCallback(() => {
    setShowMagnifier(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowMagnifier(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current) return;

      const { left, top, width, height } =
        containerRef.current.getBoundingClientRect();

      // Get cursor position relative to container
      const x = e.clientX - left;
      const y = e.clientY - top;

      // Calculate percentage position
      const xPercent = (x / width) * 100;
      const yPercent = (y / height) * 100;

      setCursorPosition({ x, y });
      setMagnifierPosition({ x: xPercent, y: yPercent });
    },
    []
  );

  return (
    <div
      ref={containerRef}
      className={`relative cursor-crosshair ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      <img src={src} alt={alt} className="w-full h-full object-cover" />

      {/* Magnifier Glass - Large circular zoom like reference */}
      {showMagnifier && (
        <div
          className="pointer-events-none absolute rounded-full overflow-hidden z-50"
          style={{
            width: `${magnifierSize}px`,
            height: `${magnifierSize}px`,
            left: cursorPosition.x - magnifierSize / 2,
            top: cursorPosition.y - magnifierSize / 2,
            backgroundImage: `url(${src})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: `${zoomLevel * 100}% ${zoomLevel * 100}%`,
            backgroundPosition: `${magnifierPosition.x}% ${magnifierPosition.y}%`,
            border: "4px solid hsl(var(--background))",
            boxShadow: `
              0 0 0 1px hsl(var(--border)),
              0 8px 32px rgba(0,0,0,0.2),
              0 16px 48px rgba(0,0,0,0.15),
              inset 0 0 0 1px rgba(255,255,255,0.1)
            `,
          }}
        />
      )}
    </div>
  );
};
